import { useLastEscalation } from '@hakimo-ui/hakimo/data-access';
import { Tooltip } from '@hakimo-ui/shared/ui-base';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getZeroToleranceTextColor } from '../sop-workflow/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  locationId: string;
  showTooltip?: boolean;
  isZeroToleranceSite?: boolean;
}

export function LastEscalationInfo({
  locationId,
  showTooltip = true,
  isZeroToleranceSite,
}: Props) {
  const {
    data: recentEscalationData,
    isLoading,
    error,
  } = useLastEscalation(locationId);

  const escalation = recentEscalationData?.escalation;

  if (isLoading) {
    return (
      <div
        className={clsx(
          'flex items-center gap-2 text-xs font-semibold',
          getZeroToleranceTextColor(isZeroToleranceSite)
        )}
      >
        <span>Loading...</span>
        {showTooltip && <InformationCircleIcon className="h-4 w-4 stroke-2" />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs font-semibold text-red-500">
        <span>Error Loading Escalation</span>
        {showTooltip && <InformationCircleIcon className="h-4 w-4 stroke-2" />}
      </div>
    );
  }

  if (!escalation?.createdAt) {
    return (
      <div
        className={clsx(
          'flex items-center gap-2 text-xs font-semibold',
          getZeroToleranceTextColor(isZeroToleranceSite)
        )}
      >
        <span>No escalation data</span>
        {showTooltip && <InformationCircleIcon className="h-4 w-4 stroke-2" />}
      </div>
    );
  }

  const locationTimezone = escalation?.location?.timezone || 'UTC';
  const escalationTime = dayjs
    .utc(escalation?.createdAt)
    .tz(locationTimezone)
    .format('YYYY-MM-DD HH:mm:ss');

  const escalationDescription = escalation?.description || 'No description';

  return (
    <div
      className={clsx(
        'flex items-center gap-2 text-xs font-semibold',
        getZeroToleranceTextColor(isZeroToleranceSite)
      )}
    >
      <span>{escalationTime}</span>
      {showTooltip && (
        <Tooltip
          text={escalationDescription}
          colorModifier="info"
          size="medium"
          position="bottom"
        >
          <InformationCircleIcon className="h-4 w-4 stroke-2" />
        </Tooltip>
      )}
    </div>
  );
}
